import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Draggable } from "react-beautiful-dnd";
import { EstimateBadge } from "@/components/common/badges/estimate-badge";
import { LabelBadge } from "@/components/common/badges/label-badge";
import { PriorityBadge } from "@/components/common/badges/priority-badge";
import AssigneeDropdown from "@/components/common/issues/assignee-dropdown";
import { Checkbox } from "@/components/ui/checkbox";
import { shouldIgnoreMouseHover } from "@/hooks/useIssueKeyboardShortcuts";
import { cn } from "@/lib/utils";
import { useContextMenuStore } from "@/store/zustand/contextMenuStore";
import { useFocusStore } from "@/store/zustand/focusStore";
import { useIssueSelectionStore } from "@/store/zustand/issueSelectionStore";
import type { Issue } from "@/types/issues";
import type { Status } from "@/types/status";
import { resolveIssueStatus } from "@/utils/filterUtils";
import type { Project } from "@/utils/types";
import DueDateBadge from "../badges/due-date-badge";
import { StatusBadge } from "../badges/status-badge";

// Configure hover delay (in milliseconds)
const HOVER_DELAY = 300; // Increased delay to give more time for mouse to settle

// Memoize badge components for better performance
const MemoizedStatusBadge = React.memo(StatusBadge);
const MemoizedPriorityBadge = React.memo(PriorityBadge);
const MemoizedEstimateBadge = React.memo(EstimateBadge);
const MemoizedLabelBadge = React.memo(LabelBadge);
const MemoizedDueDateBadge = React.memo(DueDateBadge);
const MemoizedAssigneeDropdown = React.memo(AssigneeDropdown);

interface IssueCardProps {
  issue: Issue;
  project: Project;
  canvas: any;
  statuses: Status[];
  onSelect: (project: Project, canvas: any) => void;
  icon?: any;
  issueRefs?: { [key: string]: HTMLDivElement };
  visibleIssueIds?: string[]; // For selection range calculations
  provided?: any; // Drag and drop provided prop
  snapshot?: any; // Drag and drop snapshot prop
  isDragging: boolean;
}

const IssueCard = React.forwardRef<HTMLDivElement, IssueCardProps>(
  (
    {
      issue,
      project,
      canvas,
      statuses,
      onSelect,
      icon,
      issueRefs,
      visibleIssueIds,
      provided,
      snapshot,
      isDragging,
    },
    ref,
  ) => {
    const statusNames = useMemo(() => statuses.map((s) => s.name), [statuses]);
    const selectedIssueIds = useIssueSelectionStore(
      (state) => state.selectedIssueIds,
    );
    const setLastFocusedIssue = useFocusStore(
      (state) => state.setLastFocusedIssue,
    );
    const focusedIssueId = useFocusStore((state) => state.focusedIssueId);
    const toggleIssueSelection = useIssueSelectionStore(
      (state) => state.toggleIssueSelection,
    );
    const contextMenuIssueIds = useContextMenuStore(
      (state) => state.contextMenuIssueIds,
    );

    const projectLabels = Array.isArray(project?.labels)
      ? project.labels
      : ([] as any[]);

    // The number of labels to show before starting to stack
    const LABEL_STACK_THRESHOLD = 5;
    // Once stacking starts, the number of labels to show before the stack
    const PRE_STACK_LABEL_COUNT = 4;

    const [isKeyboardActive, setIsKeyboardActive] = useState(false);

    useEffect(() => {
      const handleKeyDown = () => setIsKeyboardActive(true);
      const handleMouseMove = () => setIsKeyboardActive(false);

      window.addEventListener("keydown", handleKeyDown);
      window.addEventListener("mousemove", handleMouseMove);

      return () => {
        window.removeEventListener("keydown", handleKeyDown);
        window.removeEventListener("mousemove", handleMouseMove);
      };
    }, []);

    const isFocused = focusedIssueId === issue.canvas.id;

    const isSelected = selectedIssueIds.has(issue.canvas.id);

    // Use a ref for the hover timer
    const hoverTimerRef = useRef<NodeJS.Timeout | null>(null);

    const handleMouseEnter = useCallback(() => {
      if (shouldIgnoreMouseHover()) return;

      // Start the hover timer
      hoverTimerRef.current = setTimeout(() => {
        setLastFocusedIssue(issue.canvas.id);
      }, HOVER_DELAY);
    }, [issue.canvas.id, setLastFocusedIssue]);

    const handleMouseLeave = useCallback(() => {
      // Clear the hover timer if it exists
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = null;
      }
    }, []);

    // Cleanup timer on unmount
    useEffect(() => {
      return () => {
        if (hoverTimerRef.current) {
          clearTimeout(hoverTimerRef.current);
        }
      };
    }, []);

    const handleClick = useCallback(
      (e: React.MouseEvent) => {
        // Don't trigger click during drag
        if (snapshot?.isDragging) {
          e.preventDefault();
          return;
        }

        // Set last focused issue on any direct interaction
        setLastFocusedIssue(issue.canvas.id);
        onSelect(project, canvas);
      },
      [
        snapshot?.isDragging,
        setLastFocusedIssue,
        issue.canvas.id,
        onSelect,
        project,
        canvas,
      ],
    );

    const handleCheckboxChange = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        toggleIssueSelection(issue.canvas.id);
      },
      [toggleIssueSelection, issue.canvas.id],
    );

    const shouldHighlight = contextMenuIssueIds.includes(issue.canvas.id);

    return (
      <div className="flex flex-col">
        <div
          ref={(el) => {
            if (typeof ref === "function") {
              ref(el);
            } else if (ref) {
              ref.current = el;
            }
            if (el && issueRefs) {
              issueRefs[issue.canvas.id] = el;
            }
            // Handle drag and drop ref
            if (provided?.innerRef) {
              provided.innerRef(el);
            }
          }}
          {...provided?.draggableProps}
          {...provided?.dragHandleProps}
          className={cn(
            "w-full flex items-center justify-between min-h-9 pr-9 pl-[22px] py-2.5 gap-1 group cursor-pointer transition-all duration-200 relative",
            // Focus styling - subtle ring for keyboard navigation
            isFocused &&
              "outline-solid -outline-offset-1 outline-primary-n-600",
            isFocused && !isSelected && "bg-secondary-n-950",
            // Hover styling (different for selected vs unselected)
            !isSelected && !isKeyboardActive && "hover:bg-secondary-100",
            shouldHighlight && "bg-secondary-100",
            isSelected && "bg-primary-n-600/20",
            // Drag and drop styling
            snapshot?.isDragging &&
              "rotate-1 shadow-xl scale-105 z-50 border border-border bg-background",
            // Prevent text selection during drag
            snapshot?.isDragging && "select-none",
            // Add fade effect to selected items when a drag is happening but this item is not being dragged
            isDragging &&
              !snapshot?.isDragging &&
              isSelected &&
              "opacity-50 transition-opacity duration-200",
          )}
          data-testid="canvas-card-container"
          data-issue-id={issue.canvas.id}
          data-focused={isFocused ? "true" : "false"}
          data-selected={isSelected ? "true" : "false"}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={handleClick}
          onContextMenu={(e) => {
            if (snapshot?.isDragging) return;
            setLastFocusedIssue(issue.canvas.id);
            const selectedIds = Array.from(selectedIssueIds);
            useContextMenuStore
              .getState()
              .openContextMenuForIssues(
                selectedIssueIds.size > 0 ? selectedIds : [issue.canvas.id],
              );
          }}
        >
          {/* Add count indicator when dragging and multiple items are selected */}
          {snapshot?.isDragging && selectedIssueIds.size > 1 && isSelected && (
            <div className="absolute -top-2 -right-2 bg-secondary-n-950 text-white text-xs font-medium rounded-sm min-w-[20px] h-5 flex items-center justify-center px-1.5 ring-1 ring-secondary-n-700">
              {selectedIssueIds.size}
            </div>
          )}
          {/* Left side: Checkbox Column */}
          <div className="flex items-center gap-1 shrink-0">
            <div
              className="flex items-center justify-center w-4 h-4"
              onClick={handleCheckboxChange}
            >
              <Checkbox
                checked={isSelected}
                className={cn(
                  "h-4 w-4 rounded-sm border text-white",
                  "data-[state=checked]:bg-background-accent data-[state=checked]:border-border-light",
                  "border-secondary-n-600 hover:border-primary-400",
                  "hover:ring-1 hover:ring-primary-n-600 hover:ring-offset-1",
                )}
              />
            </div>
            <div className="flex items-start gap-1">
              <MemoizedPriorityBadge
                priority={issue.featurePrd?.priority || 0}
                issueId={issue.canvas.id}
                hasOutline={false}
              />
              <MemoizedStatusBadge
                status={statuses.find(
                  (status) =>
                    status.name ===
                    resolveIssueStatus(issue.featurePrd?.status, statusNames),
                )}
                issueId={issue.canvas.id}
                statuses={statuses}
              />
            </div>
          </div>

          {/* Middle: Feature Name */}
          <div className="flex items-start gap-1 pl-1 min-w-0 flex-1">
            <span
              className="body text-xs font-medium"
              data-testid="canvas-card-title"
            >
              {issue.featurePrd?.title || ""}
            </span>
          </div>

          {/* Right side: Labels, Estimate, Due Date, Assignee */}
          <div className="flex items-start gap-1.5 ml-4">
            {/* Label Badge */}
            {issue.canvas.labels && issue.canvas.labels.length > 0 ? (
              issue.canvas.labels.length <= LABEL_STACK_THRESHOLD ? (
                issue.canvas.labels.map((label) => (
                  <MemoizedLabelBadge
                    key={label}
                    labelId={label}
                    issue={issue}
                    projectLabels={projectLabels}
                  />
                ))
              ) : (
                <>
                  {issue.canvas.labels
                    .slice(0, PRE_STACK_LABEL_COUNT)
                    .map((label) => (
                      <MemoizedLabelBadge
                        key={label}
                        labelId={label}
                        issue={issue}
                        projectLabels={projectLabels}
                      />
                    ))}
                  <MemoizedLabelBadge
                    key={issue.canvas.labels[0]}
                    labelId={issue.canvas.labels[0]}
                    issue={issue}
                    projectLabels={projectLabels}
                    labelsToStack={(
                      issue.canvas.labels.map((label) =>
                        projectLabels?.find((l) => l.id === label),
                      ) ?? []
                    ).slice(PRE_STACK_LABEL_COUNT)}
                  />
                </>
              )
            ) : null}

            {/* Estimate Badge */}
            {issue.featurePrd?.estimate && issue.featurePrd.estimate > 0 && (
              <MemoizedEstimateBadge
                estimate={issue.featurePrd?.estimate}
                issueId={issue.canvas.id}
              />
            )}

            {/* Due Date Badge */}
            {issue.featurePrd?.due_date && issue.featurePrd.due_date !== "" && (
              <MemoizedDueDateBadge
                dueDate={issue.featurePrd.due_date}
                issueId={issue.canvas.id}
              />
            )}

            {/* Assignee */}
            <MemoizedAssigneeDropdown
              issueId={issue.canvas.id}
              users={Array.isArray(project?.users) ? project.users : []}
              value={issue.canvas.assigned_user_id ?? null}
            />
          </div>
        </div>
      </div>
    );
  },
);

IssueCard.displayName = "IssueCard";

interface DraggableIssueCardProps {
  statusIssue: Issue;
  project: Project;
  statuses: Status[];
  index: number;
  onSelect: (canvas: any) => void;
  icon?: any;
  issueRefs?: { [key: string]: HTMLDivElement };
  visibleIssueIds?: string[];
  isDragging: boolean;
}

export const DraggableIssueCard: React.FC<DraggableIssueCardProps> = ({
  statusIssue,
  project,
  statuses,
  index,
  onSelect,
  icon,
  issueRefs,
  visibleIssueIds,
  isDragging,
}) => {
  return (
    <Draggable
      draggableId={statusIssue.canvas.id}
      index={index}
      key={statusIssue.canvas.id}
    >
      {(provided, snapshot) => (
        <div>
          <IssueCard
            key={statusIssue.canvas.id}
            issue={statusIssue}
            project={project as Project}
            canvas={statusIssue.canvas}
            statuses={statuses}
            onSelect={() => onSelect(statusIssue.canvas)}
            icon={icon}
            issueRefs={issueRefs}
            visibleIssueIds={visibleIssueIds}
            provided={provided}
            snapshot={snapshot}
            isDragging={isDragging}
          />
        </div>
      )}
    </Draggable>
  );
};
